import React, { useMemo, useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { StatRow, Badge, TipoBadge, MedioBadge, SectionHeader, AlertBanner } from '../components/UI';
import { calcCaja, ventasHoy, ventasMes, promedioDiario, calcVariacionStock, calcFlujo, fmtM, fmtDate, fmtDateShort, todayStr, gcalUrl, SOCIOS, COLORS_SOCIOS, ini, costosProrrateadosSemana } from '../utils';
import { showToast } from '../components/UI';

export default function Dashboard({ store, setItem }) {
  const { movimientos, compras, costosMes, socios, semanas } = store;
  const [confirmReset, setConfirmReset] = useState(false);

  const caja = useMemo(() => calcCaja(movimientos), [movimientos]);
  const hoy = useMemo(() => ventasHoy(movimientos), [movimientos]);
  const mes = useMemo(() => ventasMes(movimientos), [movimientos]);
  const prom = useMemo(() => promedioDiario(movimientos, 20), [movimientos]);
  const stock = useMemo(() => calcVariacionStock(movimientos, compras), [movimientos, compras]);
  const flujo = useMemo(() => calcFlujo(movimientos, compras, 0), [movimientos, compras]);

  function resetStock() {
    const ajuste = stock.variacion;
    if (Math.abs(ajuste) < 1) { showToast('El stock ya está en $0'); setConfirmReset(false); return; }
    // Guardar el ajuste como movimiento especial tipo 'ajuste_stock'
    // calcVariacionStock lo excluye del cálculo via el JSON
    const id = 'mov_stock_reset_' + Date.now();
    setItem('movimientos', id, {
      id, tipo: 'ajuste_stock', fecha: todayStr(), hora: new Date().toTimeString().slice(0,5),
      monto: Math.abs(ajuste),
      monto_efectivo: 0, monto_transferencia: 0, monto_qr: 0,
      medio: 'transferencia',
      signo: ajuste > 0 ? '-' : '+',
      desc: 'Reestablecimiento variación de stock a $0', autor: 'panel'
    });
    showToast('Variación de stock reestablecida a $0');
    setConfirmReset(false);
  }

  // Alerts: vencimientos proximos
  const hoyStr = todayStr();
  const proximos7 = Object.values(compras || {}).filter(c => !c.pagado && c.fechaVenc && c.fechaVenc >= hoyStr && c.fechaVenc <= new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10));

  // Notificaciones
  const notificaciones = useMemo(() => {
    const notes = [];
    // 1. Compras vencidas
    const comprasVencidas = Object.values(compras || {}).filter(c => !c.pagado && c.fechaVenc && c.fechaVenc < hoyStr);
    if (comprasVencidas.length) notes.push({ tipo: 'error', msg: `${comprasVencidas.length} compra${comprasVencidas.length>1?'s':''} vencida${comprasVencidas.length>1?'s':''} sin pagar`, detalle: comprasVencidas.map(c => `${c.proveedor} ${fmtM(c.monto)}`).join(', ') });
    // 2. Compras proximas 3 dias
    const proximas3 = Object.values(compras || {}).filter(c => !c.pagado && c.fechaVenc && c.fechaVenc >= hoyStr && c.fechaVenc <= new Date(Date.now()+3*86400000).toISOString().slice(0,10));
    if (proximas3.length) notes.push({ tipo: 'warning', msg: `${proximas3.length} pago${proximas3.length>1?'s':''} vence${proximas3.length===1?'':'n'} en 3 días`, detalle: proximas3.map(c => `${c.proveedor} ${fmtM(c.monto)} — ${fmtDate(c.fechaVenc)}`).join(', ') });
    // 3. Préstamos hace más de 5 días
    const prestamosViejos = Object.values(movimientos || {}).filter(m => m.tipo === 'prestamo' && !m.cancelado && m.fecha && Math.floor((new Date()-new Date(m.fecha))/86400000) > 5);
    if (prestamosViejos.length) notes.push({ tipo: 'warning', msg: `${prestamosViejos.length} préstamo${prestamosViejos.length>1?'s':''} pendiente${prestamosViejos.length>1?'s':''} hace más de 5 días`, detalle: prestamosViejos.map(p => `${p.concepto} ${fmtM(p.monto)}`).join(', ') });
    // 4. Semana actual sin cerrar (si ya pasó el domingo)
    const hoyDate = new Date(); hoyDate.setHours(0,0,0,0);
    const dayOfWeek = hoyDate.getDay();
    if (dayOfWeek >= 1) { // lunes o más
      const lastMonday = new Date(hoyDate.getTime() - (dayOfWeek === 0 ? 6 : dayOfWeek-1)*86400000);
      const lmKey = lastMonday.getFullYear()+'-'+String(lastMonday.getMonth()+1).padStart(2,'0')+'-'+String(lastMonday.getDate()).padStart(2,'0');
      const semAnteriorDate = new Date(lastMonday.getTime()-7*86400000);
      const saKey = semAnteriorDate.getFullYear()+'-'+String(semAnteriorDate.getMonth()+1).padStart(2,'0')+'-'+String(semAnteriorDate.getDate()).padStart(2,'0');
      if (store.semanas && !store.semanas[saKey] && dayOfWeek >= 2) {
        notes.push({ tipo: 'info', msg: 'La semana anterior no tiene cierre de ganancias', detalle: 'Ir a Ganancias semanales para distribuir' });
      }
    }
    // 5. Costos fijos con fecha vencida sin pagar
    const costosFijosVencidos = Object.values(costosMes || {}).filter(c => !c.pagado && c.fecha && c.fecha < hoyStr && !c.esAmortizacion && c.monto > 0);
    if (costosFijosVencidos.length) notes.push({ tipo: 'warning', msg: `${costosFijosVencidos.length} costo${costosFijosVencidos.length>1?'s':''} fijo${costosFijosVencidos.length>1?'s':''} vencido${costosFijosVencidos.length>1?'s':''} sin marcar como pagado`, detalle: costosFijosVencidos.map(c => c.nombre).join(', ') });
    // 6. Caja en efectivo muy alta (más del 60% del total en efectivo)
    const cajaCalc = calcCaja(movimientos);
    if (cajaCalc.total > 0 && cajaCalc.ef / cajaCalc.total > 0.6 && cajaCalc.ef > 100000) {
      notes.push({ tipo: 'info', msg: `${((cajaCalc.ef/cajaCalc.total)*100).toFixed(0)}% del dinero está en efectivo`, detalle: `Considerá depositar ${fmtM(cajaCalc.ef * 0.4)} para tener más en cuenta` });
    }
    return notes;
  }, [compras, movimientos, costosMes, store.semanas, hoyStr]);
  const vencidos = Object.values(compras || {}).filter(c => !c.pagado && c.fechaVenc && c.fechaVenc < hoyStr);

  // Last 14 days ventas chart
  const chartData = useMemo(() => {
    const diasMap = {};
    for (let i = 13; i >= 0; i--) {
      const d = new Date(Date.now() - i * 86400000);
      const key = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
      diasMap[key] = 0;
    }
    Object.values(movimientos || {}).filter(m => m.tipo === 'venta' && diasMap[m.fecha] !== undefined).forEach(m => {
      diasMap[m.fecha] = (diasMap[m.fecha] || 0) + (parseFloat(m.monto) || 0);
    });
    return Object.entries(diasMap).map(([date, ventas]) => ({ date: fmtDateShort(date), ventas: Math.round(ventas) }));
  }, [movimientos]);

  // Last 10 movements
  const ultimos = useMemo(() =>
    Object.values(movimientos || {}).sort((a, b) => (b.fecha + (b.hora || '')).localeCompare(a.fecha + (a.hora || ''))).slice(0, 10),
    [movimientos]);

  return (
    <div>
      {/* Notificaciones */}
      {notificaciones.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          {notificaciones.map((n, i) => {
            const styles = {
              error: { bg: 'var(--red-l)', border: '#d9b0ad', color: 'var(--red)', icon: '🚨' },
              warning: { bg: 'var(--yel-l)', border: '#d4a843', color: 'var(--yel)', icon: '⚠️' },
              info: { bg: 'var(--blu-l)', border: '#a0c4e0', color: 'var(--blu-m)', icon: 'ℹ️' },
            };
            const s = styles[n.tipo] || styles.info;
            return (
              <div key={i} style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: 8, padding: '10px 14px', marginBottom: 6, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <span style={{ fontSize: 14, flexShrink: 0 }}>{s.icon}</span>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: s.color }}>{n.msg}</div>
                  {n.detalle && <div style={{ fontSize: 11, color: 'var(--tx2)', marginTop: 2 }}>{n.detalle}</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Main stats */}
      <StatRow stats={[
        { label: 'Total en caja', value: fmtM(caja.total), color: caja.total > 0 ? 'var(--grn)' : 'var(--red)', sub: `Efectivo ${fmtM(caja.ef)} | Cuentas ${fmtM(caja.cuentas)}` },
        { label: 'Ventas hoy', value: fmtM(hoy), color: 'var(--blu-m)', sub: fmtDate(hoyStr) },
        { label: 'Ventas del mes', value: fmtM(mes), sub: 'Mes actual' },
        { label: 'Promedio diario 20d', value: fmtM(prom), sub: 'Últimos 20 días' },
      ]} />

      {/* Secondary stats */}
      <StatRow stats={[
        { label: 'Solvencia proyectada', value: fmtM(flujo.solvencia), color: flujo.solvencia > 0 ? 'var(--grn)' : 'var(--red)', sub: `Al miércoles (${flujo.bounds.daysUntilWed}d)` },
        { label: 'Pagos al miércoles', value: fmtM(flujo.pagosEnPeriodo), color: 'var(--red)', sub: 'Compras a cuenta' },
        { label: 'Proyección 30d', value: fmtM(prom * 30), sub: 'En base al promedio' },
      ]} />

      {/* Stock card separada con botón reset */}
      <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', padding: '14px 16px', marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <div style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 4 }}>Variación de stock</div>
          <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'Courier, monospace', color: stock.variacion >= 0 ? 'var(--grn)' : 'var(--red)' }}>{fmtM(stock.variacion)}</div>
          <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 2 }}>Compras {fmtM(stock.compras)} − CMV {fmtM(stock.cmv)}</div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
          {!confirmReset
            ? <button onClick={() => setConfirmReset(true)} style={{ padding: '6px 12px', borderRadius: 7, border: '1px solid var(--border2)', background: 'var(--surf2)', color: 'var(--tx2)', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>↺ Reestablecer a $0</button>
            : <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => setConfirmReset(false)} style={{ padding: '6px 10px', borderRadius: 7, border: '1px solid var(--border2)', background: 'var(--surf2)', color: 'var(--tx2)', fontSize: 11, cursor: 'pointer' }}>Cancelar</button>
                <button onClick={resetStock} style={{ padding: '6px 12px', borderRadius: 7, border: 'none', background: 'var(--red)', color: '#fff', fontSize: 11, cursor: 'pointer', fontWeight: 700 }}>Confirmar</button>
              </div>
          }
        </div>
      </div>

      {/* Ventas chart */}
      <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', padding: '16px', marginBottom: 16 }}>
        <SectionHeader title="Ventas últimos 14 días" />
        <ResponsiveContainer width="100%" height={160}>
          <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
            <defs>
              <linearGradient id="colorVentas" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#1A6B45" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#1A6B45" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="date" tick={{ fontSize: 9, fill: 'var(--tx3)' }} />
            <YAxis tick={{ fontSize: 9, fill: 'var(--tx3)' }} tickFormatter={v => fmtM(v)} width={70} />
            <Tooltip formatter={v => fmtM(v)} labelStyle={{ fontSize: 11 }} contentStyle={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12 }} />
            <Area type="monotone" dataKey="ventas" stroke="#1A6B45" fill="url(#colorVentas)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Cuentas de socios */}
      <SectionHeader title="Cuentas de socios" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
        {SOCIOS.map(nombre => {
          const socioData = Object.values(socios || {}).find(s => s.nombre === nombre) || { saldo: 0, movimientos: {} };
          const totalRetiros = Object.values(movimientos || {}).filter(m => m.tipo === 'retiro' && m.socio === nombre).reduce((a, m) => a + (parseFloat(m.monto) || 0), 0);
          const disponible = (parseFloat(socioData.saldo) || 0) - totalRetiros;
          return (
            <div key={nombre} style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', padding: '14px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <div style={{ width: 30, height: 30, borderRadius: '50%', background: COLORS_SOCIOS[nombre], display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11, fontWeight: 700 }}>{ini(nombre)}</div>
                <span style={{ fontSize: 13, fontWeight: 700 }}>{nombre}</span>
              </div>
              <div style={{ display: 'flex', justify: 'space-between', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 9, color: 'var(--tx3)', textTransform: 'uppercase', fontWeight: 700, marginBottom: 2 }}>Acreditado</div>
                  <div style={{ fontFamily: 'Courier, monospace', fontWeight: 700, fontSize: 14, color: 'var(--grn)' }}>{fmtM(parseFloat(socioData.saldo) || 0)}</div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 9, color: 'var(--tx3)', textTransform: 'uppercase', fontWeight: 700, marginBottom: 2 }}>Disponible</div>
                  <div style={{ fontFamily: 'Courier, monospace', fontWeight: 700, fontSize: 14, color: disponible >= 0 ? 'var(--grn)' : 'var(--red)' }}>{fmtM(disponible)}</div>
                </div>
              </div>
              {disponible < 0 && <div style={{ fontSize: 10, color: 'var(--red)', marginTop: 5, fontWeight: 600 }}>⚠ En negativo</div>}
            </div>
          );
        })}
      </div>

      {/* Last movements */}
      <SectionHeader title="Últimos movimientos" />
      <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: 'var(--surf2)' }}>
              {['Fecha', 'Tipo', 'Descripción', 'Medio', 'Monto'].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '8px 12px', fontSize: 9.5, fontWeight: 700, color: 'var(--tx3)', borderBottom: '1px solid var(--border)', textTransform: 'uppercase', letterSpacing: '.07em' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ultimos.length === 0
              ? <tr><td colSpan={5} style={{ textAlign: 'center', padding: 36, color: 'var(--tx3)' }}>Sin movimientos registrados</td></tr>
              : ultimos.map(m => (
                <tr key={m.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '9px 12px', fontFamily: 'Courier, monospace', fontSize: 11, color: 'var(--tx3)' }}>{fmtDate(m.fecha)}{m.hora ? ` ${m.hora}` : ''}</td>
                  <td style={{ padding: '9px 12px' }}><TipoBadge tipo={m.tipo} /></td>
                  <td style={{ padding: '9px 12px', maxWidth: 200, fontSize: 12 }}>{m.desc || '-'}</td>
                  <td style={{ padding: '9px 12px' }}><MedioBadge medio={m.medio} /></td>
                  <td style={{ padding: '9px 12px', fontFamily: 'Courier, monospace', fontWeight: 700, color: m.signo === '+' ? 'var(--grn)' : 'var(--red)' }}>
                    {m.signo === '+' ? '+' : ''}{fmtM(m.monto)}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
